import axios from 'axios';
import { sortBy } from 'lodash';
import { getField, updateField } from 'vuex-map-fields';
import {
  Action,
  Module,
  Mutation,
  MutationAction,
  VuexModule,
} from 'vuex-module-decorators';
import type { ICustomer, IManagementUser } from '@license-manager/types';
import $http from '../services/api';
import { FindManyOptions } from 'typeorm/find-options/FindManyOptions';

@Module({ namespaced: true })
export default class Companies extends VuexModule {
  companiesWithCount: [ICustomer[], number] = [[], 0];
  isLoading = false;

  @Mutation
  updateField(options: { path: string; value: unknown }) {
    return updateField(this, options);
  }

  @Mutation
  setLoading(loadingState = false) {
    this.isLoading = loadingState;
  }

  @Mutation
  setCompanies(companies) {
    this.companiesWithCount = companies;
  }

  @Action
  async loadCompanies(
    payload = {
      excludefields: ['appsplashlogo', 'appcolorscheme', 'passwordconf'],
    }
  ) {
    this.context.commit('setLoading', true);
    const companiesWithCount = await $http
      .get('customers', { params: { excludefields: payload.excludefields } })
      .then((response) => response.data);
    this.context.commit('setLoading', false);
    this.context.commit('setCompanies', companiesWithCount);
  }

  @Action
  async loadComanyById({
    id = null,
    excludefields = ['appsplashlogo', 'appcolorscheme', 'passwordconf'],
  }: {
    id: number;
    excludefields?: string[];
  }) {
    this.context.commit('setLoading', true);
    const result = await $http
      .get('customers/' + id, { params: { excludefields } })
      .then((response) => response.data);
    this.context.commit('setLoading', false);
    return result;
  }

  @Action({ rawError: true })
  async updateOrCreate(payload: { id?: number; customer: ICustomer }) {
    this.context.commit('setLoading', true);

    const config = {
      headers: {
        'content-type': 'multipart/form-data',
      },
    };

    const fd = new FormData();
    if (payload.customer.appsplashlogo) {
      fd.append('appsplashlogo', payload.customer.appsplashlogo);
      delete payload.customer.appsplashlogo;
    } else if (!payload.customer.appsplashlogo) {
      // unset logo
      payload.customer.appsplashlogo = null;
    }

    fd.append('customer', JSON.stringify(payload.customer));

    try {
      if (payload.id) {
        const resp = await $http.put(
          'customers' + '/' + payload.id,
          fd,
          config
        );
      } else {
        const resp = await $http.post('customers' + '/', fd, config);
      }
      this.context.dispatch('loadCompanies');
    } finally {
      this.context.commit('setLoading', false);
    }
  }

  @Action({ rawError: true })
  async delete(id) {
    this.context.commit('setLoading', true);
    try {
      await $http.delete(`/customers/${id}`);
      this.context.dispatch('loadCompanies');
    } finally {
      this.context.commit('setLoading', false);
    }
  }


  get isStdCompany() {
    return (companyId: number) => {
      return companyId === 1;
    };
  }

  get companies() {
    return this.companiesWithCount[0];
  }

  get alphaSorted() {
    return sortBy(this.companies, (item) => item.name.toLowerCase());
  }

  get getCompanyByHCCode() {
    return (hccode) => {
      if (hccode) {
        return this.companies.find(
          (comp) => comp.hc.toLowerCase() === hccode.toLowerCase()
        );
      }
    };
  }

  get getCompanyById() {
    return (id) => {
      if (id) {
        return this.companies.find((c) => +c.id === +id);
      }
    };
  }

  get getField() {
    return getField(this);
  }
}
